import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import { useSystemsCatalog } from '../../context/SystemsCatalogContext';
import { ArrowRight, Shield, Server, Headphones, Cloud, Lock, Wrench, Network, Database, Camera, Globe, Code, Compass, X } from 'lucide-react';

const ICONS = {
  'managed-it-services': Server,
  'it-systems-management': Wrench,
  'it-help-desk-remote-support': Headphones,
  'cloud-infrastructure-support': Cloud,
  'cybersecurity-support': Shield,
  'it-infrastructure-deployment': Server,
  'network-infrastructure-management': Network,
  'backup-data-protection': Database,
  'surveillance-system-deployment': Camera,
  'website-development': Globe,
  'application-development': Code,
  'it-consulting-technology-advisory': Compass,
};

export default function SolutionsIndex() {
  const { services, orderedSlugs, loading } = useSystemsCatalog();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim().toLowerCase();

  // Filter the ordered service catalogue by free-text query when the
  // header search lands here with `?q=...`. We match against the service
  // name, slug, category, and short description so any token in any of
  // those reveals the row.
  const visibleSlugs = useMemo(() => {
    if (!q) return orderedSlugs;
    return orderedSlugs.filter((slug) => {
      const s = services[slug];
      if (!s) return false;
      const hay = [s.name, s.slug, s.category, s.short_description, s.description]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [orderedSlugs, services, q]);

  const clearQ = () => {
    const next = new URLSearchParams(params);
    next.delete('q');
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead path="/systems/solutions" title="ZHELTA Systems Services Overview" description="Comprehensive managed IT services designed to support, secure, and scale your technology infrastructure." />
      <SystemsHeader />
      <main className="py-20 md:py-28">
        <div className="zh-container max-w-6xl">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">Comprehensive IT Solutions</span>
            <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              ZHELTA Systems<br /><span className="text-[#f5a623]">Services Overview</span>
            </h1>
            <p className="mt-6 text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Comprehensive managed IT services designed to support, secure, and scale your technology infrastructure.
            </p>
          </div>

          {q && !loading && (
            <div
              className="flex items-center justify-center gap-3 mb-8 flex-wrap"
              data-testid="systems-solutions-search-summary"
            >
              <span className="text-[13px] text-zinc-600">
                {visibleSlugs.length} result{visibleSlugs.length === 1 ? '' : 's'} for{' '}
                <strong className="text-zinc-900">“{q}”</strong>
              </span>
              <button
                type="button"
                onClick={clearQ}
                data-testid="systems-solutions-search-clear"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center text-gray-500 py-16">Loading services...</div>
          ) : visibleSlugs.length === 0 ? (
            <div className="text-center py-20 text-zinc-500" data-testid="systems-solutions-search-empty">
              <p className="text-[15px] mb-3">No solutions match <strong>“{q}”</strong>.</p>
              <button
                type="button"
                onClick={clearQ}
                className="text-[13px] font-semibold text-[#c9a227] hover:underline"
              >
                Show all solutions
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleSlugs.map((slug) => {
                const s = services[slug];
                const Icon = ICONS[slug] || Server;
                return (
                  <Link
                    key={slug}
                    to={`/systems/solutions/${slug}`}
                    data-testid={`solution-card-${slug}`}
                    className="group relative rounded-2xl border border-gray-200 bg-white overflow-hidden hover:border-[#f5a623] hover:shadow-[0_8px_32px_-8px_rgba(245,166,35,0.25)] transition-all"
                  >
                    {s.image_url && (
                      <div className="relative h-36 w-full overflow-hidden bg-amber-50/40">
                        <img
                          src={s.image_url}
                          alt={s.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      {s.popular && (
                        <span className="absolute -top-2.5 left-6 px-2.5 py-0.5 rounded-full bg-[#f5a623] text-black text-[10px] font-extrabold tracking-[0.14em]">POPULAR</span>
                      )}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-[#c9a227]" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-extrabold tracking-[0.18em] text-gray-400">{s.category}</div>
                          <div className="text-base font-bold text-gray-900 leading-tight mt-0.5">{s.name}</div>
                        </div>
                      </div>
                      <p className="text-[14px] text-gray-600 leading-relaxed mb-4 min-h-[2.5rem]">{s.tagline}</p>
                      <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[#c9a227] group-hover:text-[#f5a623] transition-colors">
                        Explore Service <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="mt-20 text-center rounded-2xl border border-amber-100 bg-gradient-to-br from-[#FEFBF3] to-white p-10">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227] mb-3">Get Started Today</div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ready to Transform Your IT Infrastructure?</h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">Let&apos;s discuss how ZHELTA Systems can support your business with tailored managed IT solutions.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/systems/book-demo" data-testid="solutions-cta-book" className="px-6 py-3 rounded-full bg-[#f5a623] text-[#0e0e10] text-[13px] font-semibold uppercase tracking-wider hover:brightness-105 transition">Schedule Consultation</Link>
              <Link to="/systems/contact" data-testid="solutions-cta-contact" className="px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-semibold uppercase tracking-wider hover:brightness-110 transition">Contact Sales</Link>
            </div>
          </div>
        </div>
      </main>
      <SystemsFooter />
    </div>
  );
}
